<script setup lang="ts">
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { AxiosError } from 'axios'
import get from 'lodash/get'
import type { OpenServicesState } from '~/lib/interfaces/restaurant'
import { RestaurantAreas } from '~/lib/interfaces/restaurant'
import { CUSTOM_ERRORS_TYPES, CUSTOM_IM_ANALYTICS_ERRORS } from '~/lib/models/gtm'
import { getRestaurantStateMop, isUserFarFromCoords } from '~/utils/restaurantUtils'
import { getRestaurantOpenState } from '~/utils/restautantScheduleUtils'

const props = withDefaults(
  defineProps<{
    expanded?: boolean
    showingOnboarding?: boolean
    showFarTooltip?: boolean
  }>(),
  {
    expanded: false,
    showingOnboarding: false,
    showFarTooltip: true,
  }
)
const { t } = useLocale()
const restaurantStore = useRestaurantStore()
const { selectedRestaurant, areaSelected, restaurant } = storeToRefs(restaurantStore)
const appStore = useAppStore()
const countryCode = useCountry()
const {
  trackChangeDeliveryOptions,
  trackTooFar,
  trackCustomError,
  trackClosedRestaurant,
  trackClickChangeRestaurant,
  trackClickDontChangeRestaurant,
  trackNoCoverage,
  trackNoRider,
} = useGTM()
const { restaurantServiceRef } = useStoreService()
const restaurantService = restaurantServiceRef()

const image = computed(() =>
  areaSelected.value === RestaurantAreas.MOP
    ? '/images/icons/pickUp.svg'
    : '/images/icons/delivery.svg'
)

const showTooltip = ref(false)
const computingTooltip = ref(false)
const text = ref()
const content = {
  tKey: `onboarding.stepRestaurant${appStore.mdwConfig?.dlvActive ? '' : 'NoDlv'}`,
  action: true,
  isLast: false,
}
const restaurantTooFarFlag = ref(appStore.ecommerceConfig?.restaurantDistanceAcknowledge)
const isRestaurantClosed = ref(false)

const dlvFormattedAddress = computed(() => {
  if (!selectedRestaurant?.value?.addressDLV) return ''
  const { address, number, city, neighborhood } = selectedRestaurant.value.addressDLV
  const groups = [`${address}${number ? ' ' + number : ''}`, neighborhood || '', city].filter(
    g => g
  )
  return groups.join(', ')
})

const getTextForDlv = async (state: OpenServicesState) => {
  if (state.dlv === 'CLOSED') {
    trackClosedRestaurant()
    return t('restaurantTooltipAlert.closed')
  }

  try {
    const { tips } = await restaurantService.getRestaurantDelivery({
      area: areaSelected.value,
      latitude: String(selectedRestaurant?.value?.addressDLV?.latitude),
      longitude: String(selectedRestaurant?.value?.addressDLV?.longitude),
      countryCode,
    })
    restaurantStore.restaurantTips = tips
  } catch (err) {
    if (err instanceof AxiosError) {
      const statusCode: number = get(err, 'response.status', 500)
      switch (statusCode) {
        case 605:
          trackNoCoverage()
          return t('alertDialog.mcDeliveryError.title')
        case 613:
          trackNoRider()
          return t('restaurantTooltipAlert.riders')
      }
    }
    return ''
  }
}

const getTextForMop = (state: OpenServicesState) => {
  const mopState = getRestaurantStateMop(state)
  if (mopState === 'CLOSED') {
    isRestaurantClosed.value = true
    trackClosedRestaurant()
    return t('restaurantTooltipAlert.closed')
  }

  return ''
}

const computeStateTooltip = async () => {
  if (!restaurant?.value || !props.showFarTooltip) return

  isRestaurantClosed.value = false

  const state = getRestaurantOpenState({
    datetime: DateTime.utc(),
    active: restaurant.value.active,
    timezone: restaurant.value.timezone,
    code: restaurant.value.code,
    timePeriods: restaurant.value.timeSlotsService,
    pickupTime: appStore.ecommerceConfig?.pickupTime || 0,
  })

  restaurantStore.setRestaurantOpenState(state)

  const dlvText = RestaurantAreas.DLV === areaSelected.value && (await getTextForDlv(state))
  if (dlvText) {
    text.value = dlvText
    showTooltip.value = true
    computingTooltip.value = false
    return
  }

  const mopText = RestaurantAreas.MOP === areaSelected.value && getTextForMop(state)
  if (mopText) {
    text.value = mopText
    showTooltip.value = true
    return
  }

  const permissionStatus = await getPermissionStatus('geolocation')
  if (restaurantTooFarFlag.value && permissionStatus !== 'granted') {
    text.value = {
      title: t(`restaurantTooltipAlert.farFromRestaurantOptions.withoutGeo.title`),
      subtitle: t(`restaurantTooltipAlert.farFromRestaurantOptions.withoutGeo.subtitle`),
    }
    showTooltip.value = true
    computingTooltip.value = false
    return
  }

  const { latitude: lat, longitude: lng } = restaurant.value.coordinates
  const { isUserFarAway: userFarFromRestaurant } = await isUserFarFromCoords({
    coordinates: {
      lat,
      lng,
    },
    maxDistance: appStore.ecommerceConfig?.displayRestaurantConfirmation || 100,
  })
  if (userFarFromRestaurant) {
    text.value = restaurantTooFarFlag.value
      ? {
          title: t(`restaurantTooltipAlert.farFromRestaurantOptions.withGeo.title`),
          subtitle: t(`restaurantTooltipAlert.farFromRestaurantOptions.withGeo.subtitle`),
        }
      : t('restaurantTooltipAlert.farFromRestaurant')
    showTooltip.value = true
    // too_far
    trackCustomError({
      code: `${CUSTOM_IM_ANALYTICS_ERRORS.TOO_FAR}`,
      type: CUSTOM_ERRORS_TYPES.TOO_FAR,
    })
    trackTooFar()
  }
  computingTooltip.value = false
}

const openChangeModal = () => {
  if (restaurantTooFarFlag.value) trackClickChangeRestaurant()
  trackChangeDeliveryOptions()
  restaurantStore.openChangeRestaurantModal()
}

const closeRestaurantTooFarTooltip = () => {
  if (restaurantTooFarFlag.value) trackClickDontChangeRestaurant()
  showTooltip.value = false
}

watch(areaSelected, async (curr, prev) => {
  if (!computingTooltip.value && prev !== curr) {
    showTooltip.value = false
    text.value = ''
    computingTooltip.value = true
    await computeStateTooltip()
  }
})

watch(
  () => restaurant?.value?.code,
  async (curr, prev) => {
    if (!computingTooltip.value && prev !== curr) {
      showTooltip.value = false
      text.value = ''
      computingTooltip.value = true
      await computeStateTooltip()
    }
  }
)

onMounted(async () => {
  await computeStateTooltip()
})
</script>

<template>
  <div class="relative w-full">
    <PopperWrapper data-popper="stepRestaurant" placement="bottom">
      <div
        class="relative flex cursor-pointer items-end gap-2 rounded-full bg-mcd-secondaryIvory p-1 hover:bg-[#F2F2F2]"
        @click="openChangeModal"
      >
        <img
          :src="image"
          class="m-auto h-7"
          :alt="areaSelected === RestaurantAreas.MOP ? 'pickUp' : 'delivery'"
        />
        <div class="w-full leading-4 tracking-[-0.15px] md:w-[268px]">
          <p class="text-sm font-bold">
            {{ t(`restaurant.detail.header.${selectedRestaurant?.selectedArea || 'MOP'}`) }}
          </p>
          <p
            class="line-clamp-1 text-xs text-mcd-secondaryDarkGrey"
            :class="{ 'line-clamp-2': expanded }"
          >
            {{
              selectedRestaurant?.selectedArea === 'MOP'
                ? selectedRestaurant?.restaurant?.name
                : selectedRestaurant?.addressDLV?.alias || ''
            }}

            {{
              selectedRestaurant?.selectedArea === 'MOP'
                ? selectedRestaurant?.restaurant?.address
                : dlvFormattedAddress
            }}
          </p>
        </div>
      </div>
      <template #content="{ close }">
        <McdPopper :close="close" :content="content" />
      </template>
    </PopperWrapper>
    <template v-if="!showingOnboarding">
      <!-- far away restaurant tooltip v2 -->
      <div
        v-if="showTooltip && showFarTooltip && restaurantTooFarFlag && !isRestaurantClosed"
        class="after:content-[' '] tooltip absolute right-[5px] top-[150%] z-10 flex w-full flex-col items-start gap-2 rounded-[4px] bg-mcd-black p-4 text-white after:absolute after:bottom-[100%] after:left-[30px] after:ml-[-15px] after:block after:h-0 after:w-0 after:border-[10px] after:border-transparent after:border-b-mcd-black"
      >
        <img src="/images/icons/location-alert.svg" alt="warning" />
        <p class="w-full text-sm font-bold leading-4 tracking-[-0.15px]">
          {{ text.title }}
        </p>
        <p v-dompurify-html="text.subtitle" class="w-full text-xs leading-4 tracking-[-0.15px]" />
        <div class="flex w-full gap-2">
          <McdButton
            class="w-1/2 border-mcd-black bg-mcd-black text-[10px] font-bold text-white hover:bg-mcd-black"
            is-secondary
            @click="closeRestaurantTooFarTooltip"
          >
            {{ t('restaurantTooltipAlert.actions.dontChange') }}
          </McdButton>
          <McdButton
            class="w-1/2 border-white bg-white text-[10px] font-bold hover:bg-white"
            is-primary
            @click="openChangeModal"
          >
            {{ t('restaurantTooltipAlert.actions.change') }}
          </McdButton>
        </div>
      </div>

      <!-- far away restaurant tooltip v1 -->
      <div
        v-if="showTooltip && showFarTooltip && (!restaurantTooFarFlag || isRestaurantClosed)"
        class="after:content-[' '] tooltip absolute right-[5px] top-[150%] z-10 flex w-full items-start gap-2 rounded-[4px] bg-mcd-black p-4 text-white after:absolute after:bottom-[100%] after:left-[30px] after:ml-[-15px] after:block after:h-0 after:w-0 after:border-[10px] after:border-transparent after:border-b-mcd-black"
      >
        <img src="/images/icons/warning.svg" alt="warning" />
        <p class="w-[280px] text-sm leading-4 tracking-[-0.15px]">
          {{ text }}
        </p>
        <div class="w-4">
          <CloseIcon fill="white" width="16" height="16" @click="closeRestaurantTooFarTooltip" />
        </div>
      </div>
    </template>
  </div>
</template>
